package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.until
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.ScrimVars
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

internal const val EDITOR_NARROW_HEADER_BREAKPOINT_PX = 640

val EditorFrameStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(100.percent)
            .position(Position.Relative)
            .styleModifier {
                property("height", "100dvh")
                property("min-height", "100dvh")
                property("max-height", "100dvh")
                property("overflow", "hidden")
            }
    }
    // The skip link, styled exactly as the app shell styles its own — clip-path rather than
    // `display: none`, which would take it out of the tab order and defeat the point of it.
    cssRule(" > [data-skip-link]") {
        Modifier
            .position(Position.Absolute)
            .left(Space.LG)
            .top(Space.LG)
            .zIndex(500)
            .styleModifier {
                property("width", "1px")
                property("height", "1px")
                property("overflow", "hidden")
                property("clip-path", "inset(50%)")
                property("white-space", "nowrap")
                property("text-decoration", "none")
            }
    }
    cssRule(" > [data-skip-link]:focus") {
        Modifier
            .height(44.px)
            .padding(leftRight = Space.LG)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("width", "auto")
                property("clip-path", "none")
                property("overflow", "visible")
                property("display", "inline-flex")
                property("align-items", "center")
                property("border", "1px solid ${colorMode.toSitePalette().borderStrong}")
                property("outline-offset", "2px")
            }
    }
}

val EditorBackgroundStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(100.percent)
        .minWidth(0.px)
        .minHeight(0.px)
        .styleModifier {
            property("display", "grid")
            property("grid-template-rows", "52px minmax(0, 1fr)")
            property("overflow", "hidden")
        }
}

val EditorWorkspaceStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(100.percent)
            .minHeight(0.px)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("overflow", "hidden")
            }
    }
}

val EditorWorkbenchQueueStyle = CssStyle.base {
    Modifier.styleModifier {
        property("grid-template-columns", "${EDITOR_QUEUE_WIDTH_PX}px minmax(0, 1fr)")
    }
}

/**
 * Flex, not grid — measured, not assumed.
 *
 * This used to be `grid-template-columns: minmax(0, 1fr) auto`, banking on the `auto` track sizing
 * itself to the sidecar's own animated `width`. It does not: opening the panel in the real,
 * running app (not a test driving `advanceUtilityPresence` by hand) showed
 * `getComputedStyle(workspace).gridTemplateColumns` resolving to the sidecar's *final* width
 * (`"736px 400px"`, not a climbing sequence) the instant the transition started, because CSS
 * Grid's track-sizing algorithm reads a track's item by its specified size, not by whatever a
 * running transition currently has painted on screen — so the grid reflowed in one frame exactly
 * as before, no matter that the sidecar's own box was, in isolation, honestly transitioning.
 *
 * Flexbox does not have that layer: a flex container redistributes space to its `flex-grow`
 * siblings from whatever `flex-basis` its fixed-basis children currently have, every layout pass —
 * which is exactly "every frame" while that basis is mid-transition. `EditorUtilitySidecarStyle`
 * already declared `flex: 0 0 $EDITOR_UTILITY_TRACK`, inert under a grid parent; it is load-bearing
 * now. `EditorPaneStyle` (`flex: 1 1 auto`) is what takes up the difference.
 */
val EditorWorkbenchUtilityStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "row")
    }
}

/**
 * Queue, editor and context together — the three panes the design asks for, at the widths that fit
 * them. Below [EDITOR_TRIO_THRESHOLD_PX] the composition falls back to
 * [EditorWorkbenchUtilityStyle] and the queue yields, because something has to.
 *
 * Flex for the reason stated on [EditorWorkbenchUtilityStyle]. The queue's own fixed width
 * (`KeyPaneStyle`'s `flex: 0 0 ${EDITOR_QUEUE_WIDTH_PX}px`) does not animate, so it never needed
 * grid's help either — it was only ever sharing a row with a track that did.
 */
val EditorWorkbenchTrioStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "row")
    }
}

val EditorCompactWorkspaceStyle = CssStyle.base {
    Modifier.styleModifier {
        property("grid-template-columns", "minmax(0, 1fr)")
    }
}

val EditorFoundationFailureStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
        .minHeight(0.px)
        .styleModifier {
            property("grid-column", "1 / -1")
            property("overflow", "auto")
        }
}

/**
 * The dim behind an editor modal, on the same presence lifecycle as the panel.
 *
 * Both surfaces mounted and unmounted with no exit at all — the scrim and the dialog vanished in the
 * frame the reader pressed Escape, while every other overlay in the product eases out over
 * `OverlayExitMs`. `LModal` and the utility sheet have used the shared lifecycle for a while; these
 * two were the last callers plan 001 named and never got it.
 */
val EditorModalScrimStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .width(100.percent)
            .backgroundColor(ScrimVars.Sheet.value())
            .styleModifier {
                property("bottom", "0")
                property("z-index", "800")
                property("transition", "opacity ${Motion.OverlayEnterMs}ms ${Motion.EaseEntrance}")
            }
    }
    // Transparent on the frame it mounts, so the transition above has a value to run from.
    cssRule("[data-presence=\"entering\"]") { Modifier.opacity(0) }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.opacity(0).styleModifier {
            property("pointer-events", "none")
            property("transition", "opacity ${Motion.OverlayExitMs}ms ${Motion.EaseIn}")
        }
    }
}

val EditorModalSurfaceStyle = CssStyle {
    base {
        Modifier
        .position(Position.Fixed)
        .top(50.percent)
        .width(100.percent)
        .minWidth(0.px)
        .styleModifier {
            property("left", "50%")
            property("max-width", "560px")
            property("transform", "translate(-50%, -50%)")
            property("box-sizing", "border-box")
            property("max-height", "82dvh")
            property("overflow", "auto")
            property("padding", "24px")
            property("background", colorMode.toSitePalette().surfaceRaised.toString())
            property("color", colorMode.toSitePalette().textPrimary.toString())
            property("border", "1px solid ${colorMode.toSitePalette().borderStrong}")
            property("z-index", "801")
            property(
                "transition",
                "transform ${Motion.OverlayEnterMs}ms ${Motion.EaseEntrance}",
            )
        }
    }
    // Transform only on the way in — the fade is deliberately absent from every surface in this
    // system. The panel is centred by a translate, so its entrance composes with that rather than
    // replacing it.
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translate(-50%, -50%) translateY(${Motion.PopoverTravelPx}px) " +
                    "scale(${Motion.PopoverEnterScale})",
            )
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property(
                "transform",
                "translate(-50%, -50%) translateY(${Motion.PopoverTravelPx}px) " +
                    "scale(${Motion.PopoverEnterScale})",
            )
            property("pointer-events", "none")
            property(
                "transition",
                "opacity ${Motion.OverlayExitMs}ms ${Motion.EaseIn}, " +
                    "transform ${Motion.OverlayExitMs}ms ${Motion.EaseIn}",
            )
        }
    }
}

val EditorModalHeaderStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .gap(Space.MD)
        .padding(bottom = Space.MD)
        .styleModifier { property("border-bottom", "1px solid ${colorMode.toSitePalette().border}") }
}

val EditorModalBodyStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .padding(top = Space.MD)
        .styleModifier { property("box-sizing", "border-box") }
}

val EditorHeaderStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(EDITOR_HEADER_HEIGHT_PX.px)
            .minHeight(EDITOR_HEADER_HEIGHT_PX.px)
            .minWidth(0.px)
            .backgroundColor(colorMode.toSitePalette().surface)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .padding(leftRight = Space.MD)
            .gap(Space.SM)
            .styleModifier {
                property("box-sizing", "border-box")
                property("display", "flex")
                property("align-items", "center")
                property("flex-shrink", "0")
                // Menus and locale pickers are anchored inside the header and paint below its
                // 52px row. Clipping this flex row hides every menu item after the first; z-index
                // cannot escape an ancestor's overflow clip. The editor frame still owns viewport
                // clipping, while the header must let its overlays reach the workspace above it.
                property("overflow", "visible")
            }
    }
}

/**
 * The editor's first tab stop — and, until now, the one control in the product drawing the
 * browser's default focus ring instead of the system's.
 *
 * Measured at both 1600 and 390: `outline: 1px auto rgb(0, 95, 204)` here against
 * `2px solid rgb(68, 54, 240)` at every other stop in the same walk. It is the first thing a
 * keyboard user meets on the surface they spend the day in, so it was the worst possible place for
 * the system to visibly not apply.
 */
val EditorHeaderBackLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "6px")
                property("white-space", "nowrap")
            }
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val EditorHeaderTitleStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .displayFont()
        // Considered for HeadingSmall, as the editor's own page title deserved: reverted.
        // `EditorControllerGeometryTest`'s 320px header contract measures header.scrollWidth 322
        // against a 320 clientWidth at HeadingSmall — a 2px overflow at the narrowest supported
        // viewport, where "Back to keys", search, Assist and the overflow menu are already
        // fighting this title for room. Promoting it needs that budget re-measured, not a
        // same-commit fontSize bump.
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .styleModifier {
            property("flex", "1 1 auto")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
        }
}

/**
 * The header's progress cluster: a compact bar, then the count.
 *
 * It was a single clipped sentence — `max-width: 120px` with `text-overflow: ellipsis` — in a row
 * that also carries a project name and a locale picker, so on the widths where it drew at all it
 * often drew "9 remain…". The bar cannot be clipped into a lie and the numeral is short enough not
 * to be, so the ellipsis is gone rather than widened.
 *
 * **`MD`, not `XS`, between the bar and its numeral.** The bar's own cells are 4px apart, so a 4px
 * gap here put the count at the same distance as one more mark and the cluster read as a barcode
 * with a digit fused to its end — "⦀⦀⦀⦀9 remaining". The gap to the numeral has to be
 * unambiguously larger than the gaps inside the graphic it stands beside.
 */
val EditorHeaderProgressStyle = CssStyle {
    base {
        Modifier
            .minWidth(0.px)
            .gap(Space.MD)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .tabularNumbers()
            .styleModifier {
                property("flex", "0 0 auto")
                property("white-space", "nowrap")
            }
    }
    until(Breakpoint.SM) {
        Modifier.display(DisplayStyle.None)
    }
}

/** The bar's own track inside that cluster, sized so the numeral beside it always fits. */
val EditorHeaderProgressBarStyle = CssStyle.base {
    Modifier
        .width(96.px)
        .styleModifier { property("flex", "0 0 auto") }
}

val EditorPaneStyle = CssStyle.base {
    Modifier
        .height(100.percent)
        .minWidth(0.px)
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surface)
        .overflow(Overflow.Hidden)
        // A grid child took this for free from `minmax(0, 1fr)`; a flex child does not stretch to
        // fill remaining space unless told to. `EditorWorkbenchUtilityStyle` and
        // `EditorWorkbenchTrioStyle` are flex rows now — see the comment on the former — and this
        // is the sibling that has to give back whatever the sidecar's animated width claims. Inert
        // under the grid layouts (`EditorWorkbenchQueueStyle`, `EditorCompactWorkspaceStyle`),
        // which never read `flex` on their children at all.
        .styleModifier { property("flex", "1 1 auto") }
}

val EditorScrollStyle = CssStyle.base {
    Modifier
        .height(100.percent)
        .minHeight(0.px)
        .overflow { y(Overflow.Auto) }
        .styleModifier { property("overflow-x", "hidden") }
        .padding(topBottom = Space.XL)
        .styleModifier {
            property("overscroll-behavior", "contain")
            property("scrollbar-gutter", "stable")
        }
}
